import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Trash2, Upload } from 'lucide-react'
import { guildApi } from '@/lib/guildApi'
import { describeError } from '@/lib/auth'
import { emojiImageUrl } from '@/lib/markdown'
import { cx } from '@/lib/cx'
import type { Emoji, Guild } from '@/types/api'
import layer from '../settings/SettingsLayer.module.css'
import gs from './GuildSettingsLayer.module.css'
import styles from './EmojisSection.module.css'
import { useGuildLimits } from '@/lib/guildLimits'

const MAX_BYTES = 256 * 1024

/** Настройки сервера → «Эмодзи»: загрузка PNG/GIF/JPEG/WebP до 256 КБ, переименование, удаление. */
export function EmojisSection({ guild, canManage }: { guild: Guild; canManage: boolean }) {
  const qc = useQueryClient()
  const emojis = useQuery({ queryKey: ['emojis', guild.id], queryFn: () => guildApi.emojis(guild.id) })
  // сколько можно — по уровню бустов сервера, как считает сервер
  const slots = useGuildLimits(guild.premium_tier).emojis
  const [error, setError] = useState<string | null>(null)
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null)
  const file = useRef<HTMLInputElement | null>(null)
  const refresh = () => void qc.invalidateQueries({ queryKey: ['emojis', guild.id] })
  const create = useMutation({ mutationFn: (body: { name: string; image: string }) => guildApi.createEmoji(guild.id, body), onSuccess: refresh, onError: (err) => setError(describeError(err).fields.name ?? describeError(err).fields.image ?? describeError(err).message) })
  const rename = useMutation({ mutationFn: (v: { id: string; name: string }) => guildApi.renameEmoji(guild.id, v.id, v.name), onSuccess: () => { setRenaming(null); refresh() }, onError: (err) => setError(describeError(err).fields.name ?? describeError(err).message) })
  const remove = useMutation({ mutationFn: (id: string) => guildApi.deleteEmoji(guild.id, id), onSuccess: refresh, onError: (err) => setError(describeError(err).message) })

  const upload = (files: FileList | null) => {
    setError(null)
    for (const f of Array.from(files ?? [])) {
      if (f.size > MAX_BYTES) {
        setError(`${f.name}: больше 256 КБ`)
        continue
      }
      const reader = new FileReader()
      reader.onload = () => {
        const name = f.name.replace(/\.[^.]+$/, '').replace(/[^A-Za-z0-9_]/g, '_').slice(0, 32).padEnd(2, '_')
        create.mutate({ name, image: String(reader.result) })
      }
      reader.readAsDataURL(f)
    }
  }

  const list: Emoji[] = emojis.data ?? []
  return (
    <section>
      <h1 className={layer.sectionTitle}>Эмодзи — {list.length} из {slots}</h1>
      <ul className={layer.points}>
        <li>PNG, GIF, JPEG или WebP до 256 КБ</li>
        <li>имя от 2 до 32 символов: латиница, цифры и «_»</li>
        <li>в сообщениях — <code className={gs.code}>:имя:</code> через автодополнение, в реакциях — из пикера</li>
      </ul>
      {canManage ? (
        <div className={layer.buttonRow}>
          <input ref={file} type="file" accept="image/png,image/gif,image/jpeg,image/webp" multiple hidden onChange={(e) => { upload(e.target.files); e.target.value = '' }} />
          <button type="button" className={layer.button} disabled={create.isPending} onClick={() => file.current?.click()}><Upload size={16} /> {create.isPending ? 'Загружаем…' : 'Загрузить эмодзи'}</button>
        </div>
      ) : null}
      {error ? <p className={gs.error} role="alert">{error}</p> : null}
      {emojis.isPending ? <p className={layer.text}>Загружаем…</p> : null}
      {!emojis.isPending && list.length === 0 ? <p className={layer.text}>Эмодзи пока нет.</p> : null}
      <ul className={styles.grid}>
        {list.map((e) => (
          <li key={e.id} className={styles.card}>
            <img src={e.url ?? emojiImageUrl(e.id, e.animated)} alt={`:${e.name}:`} className={styles.img} />
            {renaming?.id === e.id ? (
              <input
                className={styles.rename}
                value={renaming.name}
                autoFocus
                maxLength={32}
                aria-label="Новое имя"
                onChange={(ev) => setRenaming({ id: e.id, name: ev.target.value })}
                onBlur={() => (renaming.name !== e.name ? rename.mutate(renaming) : setRenaming(null))}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter') rename.mutate(renaming)
                  if (ev.key === 'Escape') setRenaming(null)
                }}
              />
            ) : (
              <span className={styles.name}>:{e.name}:</span>
            )}
            {e.animated ? <span className={styles.badge}>GIF</span> : null}
            {canManage ? (
              <span className={styles.actions}>
                <button type="button" className={gs.smallButton} data-tooltip="Переименовать" aria-label={`Переименовать ${e.name}`} onClick={() => setRenaming({ id: e.id, name: e.name })}><Pencil size={14} /></button>
                <button type="button" className={cx(gs.smallButton, gs.smallDanger)} data-tooltip="Удалить" aria-label={`Удалить ${e.name}`} disabled={remove.isPending} onClick={() => remove.mutate(e.id)}><Trash2 size={14} /></button>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  )
}
